"use strict";
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
Object.defineProperty(exports, "__esModule", { value: true });
var contains_1 = require("../utils/contains");
var getListTypeFromNode_1 = require("./getListTypeFromNode");
var getTagOfNode_1 = require("../utils/getTagOfNode");
var isBlockElement_1 = require("../utils/isBlockElement");
var toArray_1 = require("../utils/toArray");
var unwrap_1 = require("../utils/unwrap");
var wrap_1 = require("../utils/wrap");
var orderListStyles = [null, 'lower-alpha', 'lower-roman'];
/**
 * @internal
 * !!! Never directly create instance of this class. It should be created within VList class !!!
 *
 * Represent a list item.
 *
 * A list item is normally wrapped using a LI tag. But this class is only a logical item,
 * it can be a LI tag, or another other type of node which means it is actually not a list item.
 * That can happen after we do "outdent" on a 1-level list item, then it becomes not a list item.
 * @internal
 */
var VListItem = /** @class */ (function () {
    /**
     * Construct a new instance of VListItem class
     * @param node The DOM node for this item
     * @param listTypes An array represnets list types of all parent and current level.
     * Skip this parameter for a non-list item.
     */
    function VListItem(node) {
        var listTypes = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            listTypes[_i - 1] = arguments[_i];
        }
        this.node = node;
        if (!node) {
            throw new Error('node must not be null');
        }
        // Always add a None list type in front of all other types to represent non-list scenario.
        this.listTypes = __spreadArrays([0 /* None */], listTypes);
    }
    /**
     * Get type of current list item
     */
    VListItem.prototype.getListType = function () {
        return this.listTypes[this.listTypes.length - 1];
    };
    /**
     * Get DOM node of this list item
     */
    VListItem.prototype.getNode = function () {
        return this.node;
    };
    /**
     * Check if a given node is contained by this list item
     * @param node The node to check
     */
    VListItem.prototype.contains = function (node) {
        return contains_1.default(this.node, node, true /*treateSameNodeAsContain*/);
    };
    /**
     * Check if this item is an orphan item.
     *
     * Orphan item is not a normal case but could happen. It represents the DOM nodes directly under OL/UL tag
     * and are in front of all other LI tags so that they cannot be merged into any existing LI tags.
     *
     * For example:
     * ```html
     * <ol>
     *   <div>Orphan node</div>
     *   <li>first item</li>
     * </ol>
     * ```
     * Here the first DIV tag is an orphan item.
     *
     * There can also be nodes directly under OL/UL but between other LI tags in source HTML which should not be
     * treated as orphan item because they can be merged into their previous LI tag. But when we build VList,
     * those nodes will be merged into LI, so that ideally here they should not exist.
     */
    VListItem.prototype.isOrphanItem = function () {
        return getTagOfNode_1.default(this.node) != 'LI';
    };
    /**
     * Check if the given item can be merged into this item.
     * An item can be merged when it is an orphan item and its list type stack is exactly the same with current one.
     * @param item The item to check
     */
    VListItem.prototype.canMerge = function (item) {
        if (!(item === null || item === void 0 ? void 0 : item.isOrphanItem()) || this.listTypes.length != item.listTypes.length) {
            return false;
        }
        return this.listTypes.every(function (type, index) { return item.listTypes[index] == type; });
    };
    /**
     * Merge items into this item.
     * @example Before merge:
     * ```html
     * <ol>
     *   <li>Current item</li>
     *   <div>line 1</div>
     *   <div>line 2</div>
     * </ol>
     * ```
     * After merge then two DIVs into LI:
     * ```html
     * <ol>
     *   <li>Current item
     *     <div>line 1</div>
     *     <div>line 2</div>
     *   </li>
     * </ol>
     * ```
     * @param items The items to merge
     */
    VListItem.prototype.mergeItems = function (items) {
        var _this = this;
        var nodesToWrap = (items === null || items === void 0 ? void 0 : items.map(function (item) { return item.node; })) || [];
        var targetNodes = wrapIfNotBlockNode(nodesToWrap, true /*checkFirst*/, false /*checkLast*/);
        targetNodes.forEach(function (node) { return _this.node.appendChild(node); });
    };
    /**
     * Indent this item
     * If this is not an list item, it will be no op
     */
    VListItem.prototype.indent = function () {
        var listType = this.getListType();
        if (listType != 0 /* None */) {
            this.listTypes.push(listType);
        }
    };
    /**
     * Outdent this item
     * If this item is already not an list item, it will be no op
     */
    VListItem.prototype.outdent = function () {
        if (this.listTypes.length > 1) {
            this.listTypes.pop();
        }
    };
    /**
     * Change list type of this item
     * @param targetType The target list type to change to
     */
    VListItem.prototype.changeListType = function (targetType) {
        if (targetType == 0 /* None */) {
            this.listTypes = [targetType];
        }
        else {
            this.outdent();
            this.listTypes.push(targetType);
        }
    };
    /**
     * Write the change result back into DOM
     * @param listStack current stack of list elements
     */
    VListItem.prototype.writeBack = function (listStack) {
        var doc = this.node.ownerDocument;
        var nextLevel = 1;
        // 1. Determine list elements that we can reuse
        // e.g.:
        //    passed in listStack: Fragment > OL > UL > OL
        //    local listTypes:     null     > OL > UL > UL > OL
        //    then Fragment > OL > UL can be reused
        for (; nextLevel < listStack.length; nextLevel++) {
            if (getListTypeFromNode_1.default(listStack[nextLevel]) !== this.listTypes[nextLevel]) {
                listStack.splice(nextLevel);
                break;
            }
        }
        // 2. Add new list elements
        // e.g.:
        //    passed in listStack: Fragment > OL > UL
        //    local listTypes:     null     > OL > UL > UL > OL
        //    then we need to create a UL and a OL tag
        for (; nextLevel < this.listTypes.length; nextLevel++) {
            var listType = this.listTypes[nextLevel];
            var newList = doc.createElement(listType == 1 /* Ordered */ ? 'ol' : 'ul');
            if (listType == 1 /* Ordered */) {
                newList.style.listStyleType =
                    orderListStyles[(nextLevel - 1) % orderListStyles.length];
            }
            listStack[listStack.length - 1].appendChild(newList);
            listStack.push(newList);
        }
        // 3. Add current node into deepest list element
        listStack[listStack.length - 1].appendChild(this.node);
        // 4. If this is not a list item now, need to unwrap the LI node and do proper handling
        if (this.listTypes.length <= 1) {
            wrapIfNotBlockNode(getTagOfNode_1.default(this.node) == 'LI' ? getChildrenAndUnwrap(this.node) : [this.node], true /*checkFirst*/, true /*checkLast*/);
        }
    };
    return VListItem;
}());
exports.default = VListItem;
function wrapIfNotBlockNode(nodes, checkFirst, checkLast) {
    if (nodes.length > 0 &&
        (!checkFirst || !isBlockElement_1.default(nodes[0])) &&
        (!checkLast || !isBlockElement_1.default(nodes[nodes.length]))) {
        nodes = [wrap_1.default(nodes)];
    }
    return nodes;
}
function getChildrenAndUnwrap(node) {
    var result = toArray_1.default(node.childNodes);
    unwrap_1.default(node);
    return result;
}
//# sourceMappingURL=VListItem.js.map